// JSA V1 Emergency Equipment Info Components
import React from "react";
import { DelDatePicker, customDatePicker } from "../../common/DelDatePicker";

import styles from "./jsaEmergencyEquipmentInfoComponents.module.css";

// Helper function to get field error for Emergency Equipment fields
const getEmergencyEquipmentFieldError = (autosaveErrors, fieldName) => {
  const key = `jsa_emergency_equipment.${fieldName}`;
  return autosaveErrors?.fieldErrors?.[key] || null;
};

// Fire Extinguisher Inspection date picker
const FireExtinguisherInspectionDatePicker = ({ callbacks }) => {
  // Handling the fire extinguisher inspection change
  const handleFireExtinguisherInspectionDateChange = (date) => {
    callbacks.v1Actions.updateFireExtinguisherInspectionDate(
      callbacks.jsaData,
      date,
      callbacks.emitPatch
    );
  };

  return (
    <div className={styles.dateContainer}>
      <label className={styles.inputLabel}>
        Fire Extinguisher Inspection Date
      </label>
      <DelDatePicker
        selected={
          callbacks.jsaData.jsaEmergencyEquipment.fireExtinguisherInspectionDate
        }
        onChange={handleFireExtinguisherInspectionDateChange}
        disabled={callbacks.delMode === "view"}
        className={styles.dateInput}
        dateFormat="MM/dd/yyyy"
        placeholderText="Select a date"
        isClearable={true}
        showPopperArrow={false}
        withPortal
        portalId="date-picker-portal"
        calendarClassName={styles.calendar}
        wrapperClassName={styles.datePickerWrapper}
      />
    </div>
  );
};

// Basic Text input for the other emergency equipment
const EmergencyEquipmentInput = ({
  label,
  updateFunction,
  jsaData,
  mode = "edit",
  value,
  emitPatch,
  autosaveErrors,
  fieldName, // Field name for error lookup
}) => {
  // Get autosave error for this field
  const fieldError = fieldName ? getEmergencyEquipmentFieldError(autosaveErrors, fieldName) : null;

  // Handling the input change (local state only on onChange)
  const handleInputChange = (e) => {
    updateFunction(jsaData, e.target.value, null); // Don't emit on every keystroke
  };

  // Handle blur - send patch to server
  const handleBlur = (e) => {
    updateFunction(jsaData, e.target.value, emitPatch); // Emit on blur
  };

  return (
    <div className={styles.otherEmergencyEquipmentContainer}>
      <label className={styles.inputLabel}>{label}</label>
      <input
        type="text"
        value={value}
        onChange={handleInputChange}
        onBlur={handleBlur}
        disabled={mode === "view"}
        className={`${styles.emergencyEquipmentInput} ${fieldError ? styles.inputError : ""}`}
      />
      {fieldError && <span className={styles.errorText}>{fieldError}</span>}
    </div>
  );
};

// JSA V1 Emergency Equipment Info Tab
export const JsaEmergencyEquipmentInfoTab = ({ callbacks }) => {
  return (
    <div className={styles.jsaEmergencyEquipmentInfoTab}>
      <div className={styles.emergencyEquipmentRow}>
        <EmergencyEquipmentInput
          label="Fire Extinguisher Type"
          updateFunction={callbacks.v1Actions.updateFireExtinguisherType}
          jsaData={callbacks.jsaData}
          mode={callbacks.jsaMode}
          value={callbacks.jsaData.jsaEmergencyEquipment.fireExtinguisherType}
          emitPatch={callbacks.emitPatch}
          autosaveErrors={callbacks.autosaveErrors}
          fieldName="fire_extinguisher_type"
        />
        <FireExtinguisherInspectionDatePicker callbacks={callbacks} />
      </div>
      <div className={styles.emergencyEquipmentRow}>
        <EmergencyEquipmentInput
          label="Fire Hydrant Location"
          updateFunction={callbacks.v1Actions.updateFireHydrantLocation}
          jsaData={callbacks.jsaData}
          mode={callbacks.jsaMode}
          value={callbacks.jsaData.jsaEmergencyEquipment.fireHydrantLocation}
          emitPatch={callbacks.emitPatch}
          autosaveErrors={callbacks.autosaveErrors}
          fieldName="fire_hydrant_location"
        />
        <EmergencyEquipmentInput
          label="Eyewash/Shower Location"
          updateFunction={
            callbacks.v1Actions.updateEmergencyEyewashShowerLocation
          }
          jsaData={callbacks.jsaData}
          mode={callbacks.jsaMode}
          value={
            callbacks.jsaData.jsaEmergencyEquipment
              .emergencyEyewashShowerLocation
          }
          emitPatch={callbacks.emitPatch}
          autosaveErrors={callbacks.autosaveErrors}
          fieldName="emergency_eyewash_shower_location"
        />
      </div>
      <div className={styles.emergencyEquipmentRow}>
        <EmergencyEquipmentInput
          label="Wind Direction"
          updateFunction={callbacks.v1Actions.updateWindDirection}
          jsaData={callbacks.jsaData}
          mode={callbacks.jsaMode}
          value={callbacks.jsaData.jsaEmergencyEquipment.windDirection}
          emitPatch={callbacks.emitPatch}
          autosaveErrors={callbacks.autosaveErrors}
          fieldName="wind_direction"
        />
        <EmergencyEquipmentInput
          label="Other Emergency Equipment"
          updateFunction={callbacks.v1Actions.updateOtherEmergencyEquipment}
          jsaData={callbacks.jsaData}
          mode={callbacks.jsaMode}
          value={
            callbacks.jsaData.jsaEmergencyEquipment.otherEmergencyEquipment
          }
          emitPatch={callbacks.emitPatch}
          autosaveErrors={callbacks.autosaveErrors}
          fieldName="other_emergency_equipment"
        />
      </div>
    </div>
  );
};
